import { Component, ChangeDetectionStrategy, inject, AfterViewInit, ElementRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { combineLatest, map } from 'rxjs';
import { FooterComponent } from '../footer/footer.component';
import { AuthService } from '../../services/auth.service';
import { CompanyInfoService } from '../../services/company-info.service';
import { ConfigService } from '../../services/config.service';

@Component({
  selector: 'app-privacy',
  standalone: true,
  imports: [CommonModule, FooterComponent],
  template: `
    <main>
      <div class="header-container">
        <div class="back-link">
          <a (click)="goBack()" style="cursor: pointer; color: oklch(51% 0.28 263.83); font-size: 0.875rem;">← Zurück</a>
        </div>
        <div class="header-content">
          <a (click)="goHome()" style="cursor: pointer; text-decoration: none; display: inline-block;">
            <h1>ProTyco</h1>
            <p style="font-size: 0.875rem; text-align: center; margin: 0.25rem 0 0 0;">
              <span style="color: oklch(51% 0.28 263.83);">PRO</span>PERTY <span style="color: oklch(51% 0.28 263.83);">TYCO</span>ON
            </p>
          </a>
        </div>
      </div>

      <div class="privacy-scroll">
      <div class="container">
        <div class="content-card">
          <h1>Datenschutzerklärung</h1>
          
          <p class="last-updated">Gültig ab: 01. Januar 2026</p>

          <section class="privacy-section">
            <h2>1. Verantwortlicher und Kontaktperson</h2>
            <p>
              {{ (companyInfo$ | async)?.name }}<br>
              {{ (representativesPersonResponsible$ | async) }}<br>
              {{ (companyInfo$ | async)?.street }}<br>
              {{ (companyInfo$ | async)?.postalCode }} {{ (companyInfo$ | async)?.city }}<br>
              {{ (companyInfo$ | async)?.country }}<br>
              E-Mail: <a [href]="'mailto:' + (companyEmail$ | async)">{{ (companyEmail$ | async) }}</a>
            </p>
          </section>

          <section class="privacy-section">
            <h2>2. Allgemeine Informationen zur Datenverarbeitung</h2>
            <p>
              Diese Datenschutzerklärung klärt Sie über die Art, den Umfang und den Zweck der 
              Verarbeitung von personenbezogenen Daten auf der {{ (companyName$ | async) }}-Plattform auf. 
              Die Verarbeitung erfolgt auf Grundlage des Bundesdatenschutzgesetzes (BDSG) und der 
              Datenschutz-Grundverordnung (DSGVO).
              Diese Datenschutzerklärung erfüllt die Informationspflichten gemäß Art. 13 DSGVO.
            </p>
          </section>

          <section class="privacy-section">
            <h2>3. Erfassung von Daten und Informationen</h2>
            <h3>3.1 Automatisch erfasste Daten</h3>
            <p>
              Beim Besuch unserer Website werden durch den Browser des Nutzers automatisch Informationen 
              wie IP-Adresse, Datum und Uhrzeit, Browsertyp und Version, Betriebssystem, Suchmaschinen-Referrer, 
              Besuchsdauer und aufgerufene Seiten erfasst (sog. Server-Log-Dateien).
            </p>
            <p>
              Rechtsgrundlage hierfür ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der technischen Sicherheit und dem störungsfreien Betrieb der Website).
            </p>
            <h3>3.2 Bei der Registrierung erfasste Daten</h3>
            <p>
              Bei Ihrer Registrierung erfassen wir folgende personenbezogene Daten:
            </p>
            <ul>
              <li>E-Mail-Adresse</li>
              <li>Passwort (ausschließlich gehasht und nicht im Klartext gespeichert)</li>
            </ul>

            <h3>3.3 Daten bei der Rechnungserstellung und Zahlungsabwicklung</h3>
            <p>
              Zum Zweck der Rechnungsstellung und Zahlungsabwicklung erfassen wir bei der Transaktion folgende Angaben, die erforderlich sind:
            </p>
            <p><strong>Rechnungsdaten:</strong></p>
            <ul>
              <li>Vorname und Nachname</li>
              <li>Ggf. Firmenname</li>
              <li>Straße und Hausnummer</li>
              <li>Postleitzahl und Stadt</li>
              <li>Land</li>
            </ul>
            <p><strong>Zahlungs- und Transaktionsdaten:</strong></p>
            <ul>
              <li>Transaktions-ID des Zahlungsdienstleisters (z. B. Stripe, PayPal)</li>
              <li>Zahlungsstatus (ausstehend, abgeschlossen, fehlgeschlagen)</li>
              <li>Zahlungsmethode und -anbieter</li>
            </ul>
            <p>
              Diese Transaktionsdaten werden gespeichert, um die Zahlungen nachvollziehbar zu dokumentieren und die ordnungsgemäße Rechnungsstellung sicherzustellen. Die Verarbeitung dieser Daten erfolgt zur Erfüllung unserer vertraglichen und gesetzlichen Pflichten gemäß Art. 6 Abs. 1 lit. b und c DSGVO.
            </p>

            <h3>3.4 Passwort und Sicherheit</h3>
            <p>
              Der Nutzer ist dafür verantwortlich, sein Passwort sicher zu verwahren. Die {{ (companyName$ | async) }} 
              übernimmt keine Haftung für unbefugte Zugriffe auf das Nutzerkonto.
            </p>
          </section>

          <section class="privacy-section">
            <h2>4. Datenverarbeitung, Zwecke und Rechtsgrundlagen</h2>
            <p>
              Die folgenden Datenverarbeitungen erfolgen auf Grundlage der jeweils angegebenen Rechtsgrundlagen gemäß Art. 6 DSGVO:
            </p>
            <div class="privacy-table-wrapper">
              <table class="privacy-table">
              <thead>
                <tr>
                  <th>Zweck der Verarbeitung</th>
                  <th>Verarbeitete Datenarten</th>
                  <th>Rechtsgrundlage</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Verwaltung des Nutzerkontos und Authentifizierung</td>
                  <td>E-Mail-Adresse, Passwort (gehasht)</td>
                  <td>Art. 6 Abs. 1 lit. b DSGVO</td>
                </tr>
                <tr>
                  <td>Bereitstellung und Verbesserung der {{ (companyName$ | async) }}-Dienstleistungen</td>
                  <td>Nutzungsdaten, technische Daten</td>
                  <td>Art. 6 Abs. 1 lit. b und lit. f DSGVO</td>
                </tr>
                <tr>
                  <td>Kommunikation mit dem Nutzer (z. B. bzgl. Updates, Systeminfos)</td>
                  <td>E-Mail-Adresse, Nutzerdaten</td>
                  <td>Art. 6 Abs. 1 lit. b DSGVO</td>
                </tr>
                <tr>
                  <td>Erfüllung gesetzlicher Pflichten und Verträge</td>
                  <td>Rechnungsdaten, Transaktionsdaten</td>
                  <td>Art. 6 Abs. 1 lit. c DSGVO</td>
                </tr>
                <tr>
                  <td>Erstellung von Rechnungen und Verwaltung von Zahlungsvorgängen zur ordnungsgemäßen Geschäftsabwicklung</td>
                  <td>Rechnungsdaten, Zahlungsdaten, Transaktions-ID</td>
                  <td>Art. 6 Abs. 1 lit. b und lit. c DSGVO</td>
                </tr>
                <tr>
                  <td>Sicherheit und Schutz vor Missbrauch</td>
                  <td>Server‑Logs, technische Aktivitätsindikatoren</td>
                  <td>Art. 6 Abs. 1 lit. f DSGVO</td>
                </tr>
                <tr>
                  <td>Analyse der Website-Nutzung in anonymisierter oder pseudonymisierter Form zur Verbesserung der Plattformfunktionen</td>
                  <td>Nutzungsdaten</td>
                  <td>Art. 6 Abs. 1 lit. f DSGVO</td>
                </tr>
                <tr>
                  <td>Marketing und Kundenkommunikation (mit vorheriger Zustimmung)</td>
                  <td>E-Mail-Adresse</td>
                  <td>Art. 6 Abs. 1 lit. a DSGVO</td>
                </tr>
              </tbody>
            </table>
            </div>
          </section>

          <section class="privacy-section">
            <h2>5. Rechtsgrundlage der Datenverarbeitung</h2>
            <p>
              Die Datenverarbeitung basiert auf folgende Rechtsgrundlagen:
            </p>
            <ul>
              <li><strong>Art. 6 Abs. 1 lit. a DSGVO:</strong> Verarbeitung mit Zustimmung des Nutzers</li>
              <li><strong>Art. 6 Abs. 1 lit. b DSGVO:</strong> Verarbeitung zur Erfüllung eines Vertrags</li>
              <li><strong>Art. 6 Abs. 1 lit. c DSGVO:</strong> Verarbeitung zur Erfüllung gesetzlicher Verpflichtungen</li>
              <li><strong>Art. 6 Abs. 1 lit. f DSGVO:</strong> Verarbeitung zum Zwecke berechtigter Interessen</li>
            </ul>
          </section>

          <section class="privacy-section">
            <h2>6. Speicherdauer</h2>
            <p>
              Die personenbezogenen Daten werden gelöscht, wenn sie für die Zwecke der Verarbeitung 
              nicht mehr erforderlich sind. Dies gilt insbesondere dann, wenn das Nutzerkonto gelöscht wird 
              oder wenn die rechtliche Aufbewahrungspflicht entfällt. Allerdings können wir die Daten 
              danach noch für steuerliche oder andere gesetzliche Zwecke speichern müssen.
            </p>
          </section>

          <section class="privacy-section">
            <h2>7. Datenweitergabe an Dritte</h2>
            <p>
              {{ (companyName$ | async) }} gibt Ihre personenbezogenen Daten nicht ohne Ihre Zustimmung an Dritte weiter, 
              es sei denn, dass:
            </p>
            <ul>
              <li>wir dazu gesetzlich verpflichtet sind</li>
              <li>es zur Erfüllung des Nutzungsverhältnisses erforderlich ist</li>
              <li>Sie eine Zustimmung erteilt haben</li>
              <li>es zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist</li>
            </ul>
            <p>
              Insbesondere verwenden wir externe Dienstleister für:
            </p>
            <ul>
              <li>Zahlungsverarbeitung (z. B. Stripe, PayPal)</li>
              <li>Hosting und IT-Infrastruktur</li>
              <li>E-Mail-Versand</li>
              <li>Künstliche Intelligenz und KI-Berechnungen</li>
            </ul>

            <h3>Auftragsverarbeiter und Drittanbieter</h3>
            <p>
              {{ (companyName$ | async) }} arbeitet bei der Erbringung von Dienstleistungen mit folgenden Auftragsverarbeitern und Drittanbietern zusammen:
            </p>

            <h3>Zahlungsabwicklung</h3>
            <p><strong>Stripe Inc.:</strong> Verarbeitung von Kreditkartenzahlungen</p>
            <ul>
              <li>Standort: USA (mit Datenschutzgarantien)</li>
              <li>Daten: Zahlungsinformationen, E-Mail, Rechnungsadresse, Transaktions-ID</li>
            </ul>

            <p><strong>PayPal Europe S.à.r.L.:</strong> Alternative Zahlungsmethode</p>
            <ul>
              <li>Standort: Europa/USA (mit Datenschutzgarantien)</li>
              <li>Daten: Zahlungsinformationen, E-Mail, Rechnungsadresse</li>
            </ul>

            <h3>Künstliche Intelligenz</h3>
            <p><strong>Google Gemini API:</strong> KI-gestützte Berechnungen für Immobilienanalysen</p>
            <ul>
              <li>Standort: USA (mit Datenschutzgarantien)</li>
              <li>Daten: Eingabeparameter für Berechnungen (PLZ, Straße, Größe, etc.), NICHT personenbezogene Daten</li>\n            </ul>

            <p><strong>Groq API:</strong> Alternative KI-Verarbeitung für Immobilienanalysen</p>
            <ul>
              <li>Standort: USA (mit Datenschutzgarantien)</li>
              <li>Daten: Eingabeparameter für Berechnungen (PLZ, Straße, Größe, etc.), NICHT personenbezogene Daten</li>
            </ul>

            <h3>Hosting und IT-Infrastruktur</h3>
            <p><strong>STRATO AG:</strong> Hosting der {{ (companyName$ | async) }}-Plattform</p>
            <ul>
              <li>Standort: EU</li>
              <li>Daten: Server-Logs, Zugriffsdaten, Website-Nutzung</li>
            </ul>

            <h3>E-Mail-Versand</h3>
            <p><strong>E-Mail-Service:</strong> Versand von Verifikationscodes und Benachrichtigungen</p>
            <ul>
              <li>Standort: EU</li>
              <li>Daten: E-Mail-Adresse, Verifikationscode</li>
            </ul>

            <h3>Datenübertragungen in Drittländer</h3>
            <p>
              Einige unserer Auftragsverarbeiter (Stripe, Google Gemini, Groq) befinden sich in den USA. 
              Diese Unternehmen unterliegen adäquaten Datenschutzsmechanismen (EU-US Data Privacy Framework, Standardvertragsklauseln).<br>
              Es kann dennoch nicht vollständig ausgeschlossen werden, dass US‑Behörden auf Daten zugreifen, ohne dass wirksame Rechtsbehelfe bestehen.
            </p>
          </section>

          <section class="privacy-section">
            <h2>8. Cookies und Tracking-Technologien</h2>
            <p>
              {{ (companyName$ | async) }} nutzt <strong>KEINE HTTP-Cookies</strong> zur Verfolgung von Nutzern.
            </p>
            <h3>Was wir stattdessen nutzen:</h3>
            <ul>
              <li><strong>SessionStorage:</strong> Zur Speicherung von Authentifizierungs-Token während der Sitzung</li>
            </ul>
            <h3>Benutzeraktivitäts-Überwachung:</h3>
            <p>
              Wir erfassen ausschließlich technische Aktivitätsindikatoren zur Erkennung von Inaktivität (z. B. Mausbewegung vorhanden/nicht vorhanden). Inhalte von Tastatur- oder Texteingaben werden nicht gespeichert. Diese Daten werden:
            </p>
            <ul>
              <li>NICHT an externe Services weitergegeben</li>
              <li>NICHT für Analyse oder Marketing verwendet</li>
              <li>Nur zur Sicherung Ihrer Session genutzt</li>
            </ul>
          </section>

          <section class="privacy-section">
            <h2>9. Sicherheit und Datenschutz</h2>
            <p>
              {{ (companyName$ | async) }} wendet angemessene technische und organisatorische Maßnahmen an, um 
              Ihre personenbezogenen Daten vor unbefugtem Zugriff, Änderung, Verlust oder 
              Zerstörung zu schützen. Dies umfasst:
            </p>
            <ul>
              <li>SSL/TLS-Verschlüsselung</li>
              <li>Sichere Passwort-Speicherung</li>
              <li>Regelmäßige Sicherheitsupdates</li>
              <li>Zugriffskontrolle und Authentifizierung</li>
            </ul>
          </section>

          <section class="privacy-section">
            <h2>10. Betroffenenrechte</h2>
            <p>
              Sie haben gegenüber {{ (companyName$ | async) }} folgende Rechte:
            </p>
            <ul>
              <li><strong>Auskunftsrecht (Art. 15 DSGVO):</strong> Recht, Auskunft über Ihre Daten zu erhalten</li>
              <li><strong>Berichtigungsrecht (Art. 16 DSGVO):</strong> Recht auf Korrektur unrichtiger Daten</li>
              <li><strong>Löschungsrecht (Art. 17 DSGVO):</strong> Recht auf Löschung ("Recht auf Vergessenwerden")</li>
              <li><strong>Einschränkungsrecht (Art. 18 DSGVO):</strong> Recht auf Einschränkung der Verarbeitung</li>
              <li><strong>Datenportabilität (Art. 20 DSGVO):</strong> Recht auf Erhalt Ihrer Daten in strukturierter Form</li>
              <li><strong>Widerspruchsrecht (Art. 21 DSGVO):</strong> Recht auf Widerspruch gegen die Verarbeitung</li>
            </ul>
            <p>
              Zur Ausübung dieser Rechte kontaktieren Sie uns unter der obigen Adresse.
            </p>
          </section>

          <section class="privacy-section">
            <h2>11. Beschwerderecht</h2>
            <p>
              Sie haben das Recht, bei einer Datenschutzbehörde eine Beschwerde einzureichen, 
              wenn Sie der Meinung sind, dass die Verarbeitung Ihrer personenbezogenen Daten gegen Datenschutzrecht verstößt. Die zuständige Behörde ist:
            </p>
            <p>
              Berliner Beauftragte für Datenschutz und Informationsfreiheit<br>
              E-Mail: <a href="mailto:mailbox@senbjf.berlin.de">mailbox@senbjf.berlin.de</a><br>
              Internet: <a href="https://www.datenschutz-berlin.de" target="_blank">www.datenschutz-berlin.de</a>
            </p>
          </section>

          <section class="privacy-section">
            <h2>12. Externe Links</h2>
            <p>
              Diese Datenschutzerklärung gilt nur für Inhalte, die von {{ (companyName$ | async) }} betrieben werden. 
              Externe Links führen auf Websites, die nicht unter unserer Kontrolle stehen. 
              Für den Datenschutz dieser Websites sind die jeweiligen Betreiber verantwortlich.
            </p>
          </section>

          <section class="privacy-section">
            <h2>13. Änderungen der Datenschutzerklärung</h2>
            <p>
              {{ (companyName$ | async) }} behält sich vor, diese Datenschutzerklärung an gesetzliche Anforderungen, technische Entwicklungen oder Funktionsänderungen der Plattform anzupassen.
            </p>
          </section>

          <section class="privacy-section">
            <h2>14. Kontakt und Datenverantwortlicher</h2>
            <p>
              Bei Fragen zum Datenschutz oder zur Ausübung Ihrer Betroffenenrechte kontaktieren Sie uns:
            </p>
            <p>
              {{ (companyInfo$ | async)?.name }}<br>
              {{ (representativesPersonResponsible$ | async) }}<br>
              {{ (companyInfo$ | async)?.street }}<br>
              {{ (companyInfo$ | async)?.postalCode }} {{ (companyInfo$ | async)?.city }}<br>
              {{ (companyInfo$ | async)?.country }}<br>
              E-Mail: <a [href]="'mailto:' + (companyEmail$ | async)">{{ (companyEmail$ | async) }}</a>
            </p>
          </section>
        </div>
      </div>
      </div>
    </main>

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0 0 1.5rem;
      min-height: 0;
    }

    .privacy-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 22px 1.5rem 0;
    }

    .header-container {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      background: white;
      color: white;
      padding: 1rem 2rem 1rem 2rem;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
      border-bottom: 1px solid #d1d5db;
      position: relative;
      z-index: 10;
      overflow: visible;
    }

    .header-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
      pointer-events: none;
    }

    .back-link {
      position: absolute;
      left: 5px;
      top: 37px;
      z-index: 10;
      pointer-events: none;
    }

    .back-link a {
      pointer-events: auto;
    }

    .back-link a:hover {
      opacity: 0.7;
      text-decoration: underline;
    }

    .back-link a:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .header-content {
      flex: 1;
      text-align: center;
      position: relative;
      overflow: visible;
    }

    .header-content::before {
      content: '';
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 40px;
      background: linear-gradient(135deg, oklch(60% 0.3 280) 0%, oklch(45% 0.28 263.83) 100%);
      border-radius: 50%;
      opacity: 0.1;
      filter: blur(20px);
      z-index: 0;
    }

    .header-content h1 {
      background: linear-gradient(135deg, oklch(45% 0.28 263.83) 0%, oklch(60% 0.3 280) 50%, oklch(45% 0.28 263.83) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-size: 3rem;
      margin: 0;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      font-weight: 900;
      letter-spacing: 0px;
      position: absolute;
      top: -32px;
      left: 50%;
      transform: translateX(calc(-50% + 7px));
      white-space: nowrap;
    }

    .header-content p {
      color: oklch(28% 0.06 263.83);
      margin: 43px 0 0 15px !important;
      padding-bottom: 0rem;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 1px;
      text-transform: uppercase;
      line-height: 1.4;
    }

    .header-content p span {
      position: relative;
    }

    .header-content a:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .container {
      flex: 1;
      max-width: 900px;
      margin: 0 auto;
      width: 100%;
    }

    .content-card {
      background-color: white;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      padding: 2rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .content-card h1 {
      margin-top: 0;
      color: #1f2937;
      border-bottom: 2px solid oklch(51% 0.28 263.83);
      padding-bottom: 1rem;
    }

    .last-updated {
      color: #6b7280;
      font-size: 0.875rem;
      margin-bottom: 2rem;
      font-style: italic;
    }

    .privacy-section {
      margin-bottom: 2rem;
    }

    .privacy-section h2 {
      color: oklch(51% 0.28 263.83);
      margin-top: 0;
      margin-bottom: 1rem;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 0.5rem;
    }

    .privacy-section h3 {
      color: #374151;
      margin-top: 1.5rem;
      margin-bottom: 0.5rem;
      font-size: 1.1rem;
    }

    .privacy-section p {
      color: #6b7280;
      line-height: 1.6;
      margin-bottom: 1rem;
    }

    .privacy-section ul {
      margin: 1rem 0 1rem 2rem;
      color: #6b7280;
      line-height: 1.8;
    }

    .privacy-section li {
      margin-bottom: 0.5rem;
    }

    .privacy-section a {
      color: oklch(51% 0.28 263.83);
      text-decoration: none;
    }

    .privacy-section a:hover {
      text-decoration: underline;
    }

    .privacy-table {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
      border: 1px solid #e5e7eb;
    }

    .privacy-table thead {
      background-color: #f9fafb;
    }

    .privacy-table th {
      border: 1px solid #e5e7eb;
      padding: 0.75rem;
      text-align: left;
      font-weight: 600;
      color: #1f2937;
    }

    .privacy-table td {
      border: 1px solid #e5e7eb;
      padding: 0.75rem;
      color: #6b7280;
      line-height: 1.6;
    }

    .privacy-table tbody tr:nth-child(even) {
      background-color: #f9fafb;
    }

    .privacy-table-wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      margin: 1rem 0;
      border: 1px solid #e5e7eb;
      border-radius: 0.375rem;
    }

    @media (max-width: 768px) {
      main {
        padding: 0 0 1rem;
        gap: 0;
      }

      .privacy-scroll {
        padding: 22px 1rem 0;
      }

      .header-content h1 {
        font-size: 2rem;
        padding-top: 1rem;
      }

      .header-content p {
        font-size: 0.5625rem !important;
        margin: 32px 0 0 15px !important;
      }

      .content-card {
        padding: 1rem;
      }

      .privacy-section h2 {
        font-size: 1.25rem;
      }

      .privacy-section h3 {
        font-size: 1rem;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrivacyComponent implements AfterViewInit {
  private location = inject(Location);
  private router = inject(Router);
  private authService = inject(AuthService);
  private elementRef = inject(ElementRef);
  private configService = inject(ConfigService);
  companyInfoService = inject(CompanyInfoService);
  companyInfo$ = this.companyInfoService.companyInfo$;
  companyName$ = this.configService.getConfig('Company:Name');
  representativesPersonResponsible$ = combineLatest([
    this.configService.getConfig('Company:VertretenDurchLabel'),
    this.configService.getConfig('Company:VertretenDurch')
  ]).pipe(
    map(([label, value]) => {
      if (!label && !value) return null;
      if (label && value) return `${label} ${value}`;
      return label || value;
    })
  );
  companyEmail$ = this.configService.getConfig('Company:Email');

  ngAfterViewInit() {
    setTimeout(() => {
      window.scrollTo(0, 0);
      const scrollElement = this.elementRef.nativeElement.querySelector('.privacy-scroll');
      if (scrollElement) {
        scrollElement.scrollTop = 0;
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
  }

  goBack() {
    this.location.back();
  }

  goHome() {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/calculator']);
    } else {
      this.router.navigate(['/']);
    }
  }
}
